/**
 * GDPR-Compliant Cookie Consent Banner
 */

import { useState, useEffect } from 'react';
import { saveTrackingConsent, hasTrackingConsent } from '../utils/deviceTracking';

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(true);
  const [marketingConsent, setMarketingConsent] = useState(false);

  useEffect(() => {
    // Show banner if user hasn't given consent yet
    const hasConsent = hasTrackingConsent();
    setShowBanner(!hasConsent);
  }, []);

  const handleAcceptAll = () => {
    saveTrackingConsent(true, true);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    saveTrackingConsent(false, false);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    saveTrackingConsent(analyticsConsent, marketingConsent);
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {!showSettings ? (
          // Simple consent banner
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                🍪 Utilizziamo i cookie
              </h3>
              <p className="text-sm text-gray-600">
                Utilizziamo cookie e tecnologie simili per migliorare la tua esperienza,
                analizzare l'utilizzo del sito e personalizzare i contenuti.
                I cookie analitici ci aiutano a capire come usi il servizio per migliorarlo.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <button
                onClick={() => setShowSettings(true)}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Personalizza
              </button>
              <button
                onClick={handleRejectAll}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Rifiuta tutto
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Accetta tutto
              </button>
            </div>
          </div>
        ) : (
          // Detailed settings
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                Impostazioni Cookie
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Essential Cookies */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">Cookie Essenziali</h4>
                <div className="px-3 py-1 bg-gray-400 text-white text-xs font-medium rounded-full">
                  Sempre attivi
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Questi cookie sono necessari per il funzionamento del sito web e non possono essere disattivati.
                Includono informazioni sulla sessione e l'autenticazione.
              </p>
            </div>

            {/* Analytics Cookies */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">Cookie Analitici</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={analyticsConsent}
                    onChange={(e) => setAnalyticsConsent(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
              <p className="text-sm text-gray-600">
                Ci aiutano a capire come i visitatori interagiscono con il sito raccogliendo e
                riportando informazioni in modo anonimo. Questo ci permette di migliorare il servizio.
              </p>
            </div>

            {/* Marketing Cookies */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">Cookie di Marketing</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
              <p className="text-sm text-gray-600">
                Utilizzati per tracciare i visitatori attraverso i siti web e mostrare annunci
                pertinenti. Permettono di inviarti offerte personalizzate.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleRejectAll}
                className="flex-1 px-6 py-3 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Rifiuta tutto
              </button>
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-6 py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Salva preferenze
              </button>
            </div>

            {/* Privacy Policy Link */}
            <p className="text-xs text-gray-500 text-center">
              Per maggiori informazioni, consulta la nostra{' '}
              <a href="/privacy" className="text-indigo-600 hover:underline">
                Informativa sulla Privacy
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
